import { useQuery } from '@tanstack/react-query';
import { CalendarDays, CheckCircle2, Share2 } from 'lucide-react';
import { Link } from 'wouter';

import { PublicShell } from '@/components/public-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { authedJson } from '@/lib/http';
import { formatDateWithTimezone } from '@/lib/dateUtils';

interface CheckoutSessionSummaryResponse {
  id: string;
  class: {
    id: string;
    title: string;
    creator: {
      displayName: string;
    } | null;
  };
  session: {
    id: string;
    startDateTime: string;
    endDateTime: string;
  };
}

interface PassClassResponse {
  id: string;
  title: string;
  creator: { displayName: string } | null;
}
interface PassSessionResponse {
  id: string;
  startDateTime: string;
}

export default function CheckoutConfirmation() {
  const searchParams = new window.URLSearchParams(window.location.search);
  const checkoutSessionId = searchParams.get('checkout_session_id');
  // Punch-pass redemption confirmation params.
  const isPass = searchParams.get('pass') === '1';
  const passClassId = searchParams.get('class_id');
  const passSessionId = searchParams.get('session_id');
  const passRemaining = searchParams.get('remaining');

  const { data } = useQuery<CheckoutSessionSummaryResponse | null>({
    queryKey: ['checkout-confirmation', checkoutSessionId],
    queryFn: async () => {
      if (!checkoutSessionId) {
        return null;
      }

      return authedJson<CheckoutSessionSummaryResponse>(
        `/api/checkout/sessions/${checkoutSessionId}`
      );
    },
    enabled: !!checkoutSessionId,
  });

  const { data: passClass } = useQuery<PassClassResponse | null>({
    queryKey: ['pass-confirmation-class', passClassId],
    queryFn: async () => authedJson<PassClassResponse>(`/api/classes/${passClassId}`),
    enabled: isPass && !!passClassId,
  });

  const { data: passSessions } = useQuery<PassSessionResponse[]>({
    queryKey: ['pass-confirmation-sessions', passClassId],
    queryFn: async () => authedJson<PassSessionResponse[]>(`/api/classes/${passClassId}/sessions`),
    enabled: isPass && !!passClassId,
  });
  const passSession = passSessions?.find(s => s.id === passSessionId);

  if (isPass) {
    return (
      <PublicShell routeKey="checkout-confirmation">
        <section className="page-shell-narrow py-12">
          <Card className="rounded-[2rem] border-border/70 shadow-sm">
            <CardContent className="space-y-6 p-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <div className="space-y-3">
                <p className="editorial-kicker">Punch used</p>
                <h1 className="text-4xl font-semibold">You’re booked.</h1>
                <p className="section-copy">
                  A punch from your pass booked this session.
                  {passRemaining !== null && passRemaining !== ''
                    ? ` You have ${passRemaining} ${
                        passRemaining === '1' ? 'punch' : 'punches'
                      } left.`
                    : ''}
                </p>
              </div>

              {passClass ? (
                <div className="rounded-[1.5rem] border border-border/70 bg-muted/30 p-5 text-left">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                      {passClass.creator?.displayName ?? 'Kalovate'}
                    </Badge>
                    {passSession && (
                      <Badge variant="outline" className="rounded-full px-3 py-1">
                        {formatDateWithTimezone(passSession.startDateTime, { dateOnly: true })}
                      </Badge>
                    )}
                  </div>
                  <h2 className="mt-4 text-2xl font-semibold">{passClass.title}</h2>
                  {passSession && (
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      Starts {formatDateWithTimezone(passSession.startDateTime)}.
                    </p>
                  )}
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <Link href="/account/enrollments">
                  <Button className="rounded-full px-5">My classes</Button>
                </Link>
                {passClassId && (
                  <Link href={`/classes/${passClassId}`}>
                    <Button variant="outline" className="rounded-full px-5">
                      Back to class
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </section>
      </PublicShell>
    );
  }

  return (
    <PublicShell routeKey="checkout-confirmation">
      <section className="page-shell-narrow py-12">
        <Card className="rounded-[2rem] border-border/70 shadow-sm">
          <CardContent className="space-y-6 p-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <div className="space-y-3">
              <p className="editorial-kicker">Enrollment complete</p>
              <h1 className="text-4xl font-semibold">You’re booked.</h1>
              <p className="section-copy">
                Your confirmation is recorded in your account, and your class reminders will follow
                the notification settings you chose.
              </p>
            </div>

            {data ? (
              <div className="rounded-[1.5rem] border border-border/70 bg-muted/30 p-5 text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                    {data.class.creator?.displayName ?? 'Kalovate'}
                  </Badge>
                  <Badge variant="outline" className="rounded-full px-3 py-1">
                    {formatDateWithTimezone(data.session.startDateTime, { dateOnly: true })}
                  </Badge>
                </div>
                <h2 className="mt-4 text-2xl font-semibold">{data.class.title}</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Starts {formatDateWithTimezone(data.session.startDateTime)}.
                </p>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-3">
              <Link href="/account/enrollments">
                <Button className="rounded-full px-5">My classes</Button>
              </Link>
              <Link href="/classes">
                <Button variant="outline" className="rounded-full px-5">
                  Browse more
                </Button>
              </Link>
              <Button
                variant="ghost"
                className="rounded-full px-5"
                onClick={() => {
                  void navigator.clipboard.writeText(window.location.href);
                }}
              >
                <Share2 className="mr-2 h-4 w-4" />
                Copy link
              </Button>
            </div>

            <div className="rounded-[1.5rem] border border-border/70 bg-card p-5 text-left">
              <div className="flex items-center gap-3">
                <CalendarDays className="h-5 w-5 text-primary" />
                <p className="font-medium text-foreground">Next step</p>
              </div>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                View your upcoming session in your account and keep exploring what else is happening
                nearby.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>
    </PublicShell>
  );
}
